import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import {
    fetchCampaignHostMine,
    fetchCampaignHostPricing,
    verifyCampaignHostPaystack,
} from '../../redux/campaignHostSubscription/actions';
import type { ICampaignHostVerifyPaystackResult } from '../../types/global/campaignHostSubscription.types';
import { isActiveCampaignHostSubscription } from '../../utils/campaignHostSubscriptionFormat';
import {
    clearCampaignHostPaystackReference,
    clearCampaignHostReturnUrl,
    readCampaignHostCheckoutProvider,
    readCampaignHostPaystackReference,
    readCampaignHostReturnUrl,
} from '../../utils/campaignHostSubscriptionRailUtils';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { isFlutterwaveAbandonedReturn } from '../../utils/flutterwaveReturn';

function pickPaystackReference(searchParams: URLSearchParams): string | null {
    return searchParams.get('reference') ?? searchParams.get('trxref') ?? readCampaignHostPaystackReference();
}

function pickFlutterwavePayload(searchParams: URLSearchParams): {
    transaction_id?: string;
    tx_ref?: string;
    reference?: string;
} | null {
    const transaction_id = searchParams.get('transaction_id') ?? undefined;
    const tx_ref =
        searchParams.get('tx_ref') ?? searchParams.get('reference') ?? readCampaignHostPaystackReference() ?? undefined;
    if (!transaction_id && !tx_ref) return null;
    return { transaction_id, tx_ref, reference: tx_ref };
}

function isVerified(result: ICampaignHostVerifyPaystackResult | null | undefined): boolean {
    if (!result) return false;
    if (result.verified === true) return true;
    return isActiveCampaignHostSubscription(result.subscription);
}

export default function BrandCampaignHostSubscriptionCallbackPage() {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [searchParams] = useSearchParams();
    const retried = useRef(false);
    const [status, setStatus] = useState<'verifying' | 'success' | 'pending' | 'error'>('verifying');
    const [message, setMessage] = useState('Confirming your subscription…');

    const finishSuccess = useCallback(() => {
        clearCampaignHostPaystackReference();
        const returnUrl = readCampaignHostReturnUrl();
        clearCampaignHostReturnUrl();
        dispatch(fetchCampaignHostMine());
        dispatch(fetchCampaignHostPricing());
        showToastMessage('Subscription active', 'success');
        setStatus('success');
        setMessage('Payment confirmed. Redirecting…');
        setTimeout(() => navigate(returnUrl || ROUTES.BRAND_SETTINGS, { replace: true }), 1500);
    }, [dispatch, navigate]);

    useEffect(() => {
        if (isFlutterwaveAbandonedReturn(searchParams)) {
            const returnUrl = readCampaignHostReturnUrl();
            clearCampaignHostReturnUrl();
            navigate(returnUrl || ROUTES.BRAND_SETTINGS, { replace: true });
            return;
        }
        const useFlutterwave =
            Boolean(searchParams.get('transaction_id') || searchParams.get('tx_ref')) ||
            readCampaignHostCheckoutProvider() === 'flutterwave';

        const verify = async (retry: boolean) => {
            try {
                let result: ICampaignHostVerifyPaystackResult;
                if (useFlutterwave) {
                    const fw = pickFlutterwavePayload(searchParams);
                    if (!fw) {
                        setStatus('error');
                        setMessage('Missing payment reference. Return to subscription settings and try again.');
                        return;
                    }
                    result = (await dispatchPromise(
                        verifyCampaignHostPaystack({ ...fw, provider: 'flutterwave' }),
                    )) as ICampaignHostVerifyPaystackResult;
                } else {
                    const reference = pickPaystackReference(searchParams);
                    if (!reference) {
                        setStatus('error');
                        setMessage('Missing payment reference. Return to subscription settings and try again.');
                        return;
                    }
                    result = (await dispatchPromise(
                        verifyCampaignHostPaystack({ reference, provider: 'paystack' }),
                    )) as ICampaignHostVerifyPaystackResult;
                }

                if (isVerified(result)) {
                    finishSuccess();
                    return;
                }

                if (!retry && !retried.current) {
                    retried.current = true;
                    setMessage('Payment processing… checking again in a moment.');
                    await new Promise(r => setTimeout(r, 2500));
                    await verify(true);
                    return;
                }

                setStatus('pending');
                setMessage(
                    'Payment received but still processing. Your plan will activate shortly — refresh Settings in a few seconds.',
                );
            } catch (err: unknown) {
                const msg =
                    (err as { data?: { message?: string } })?.data?.message ??
                    (err as Error)?.message ??
                    'Could not verify payment.';
                setStatus('error');
                setMessage(msg);
            }
        };

        void verify(false);
    }, [searchParams, dispatchPromise, finishSuccess, navigate]);

    return (
        <MetaTags title="Subscription payment | Brand | Thspian" description="Confirming campaign host subscription">
            <Box
                sx={{
                    minHeight: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    px: 3,
                    textAlign: 'center',
                }}>
                {status === 'verifying' ? <CircularProgress sx={{ color: t.primary_orange }} /> : null}
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: t.primary_white }}>
                    {status === 'success' ? 'Subscription confirmed' : 'Campaign host subscription'}
                </Typography>
                <Typography sx={{ fontSize: 14, color: t.form_label, maxWidth: 420, lineHeight: 1.5 }}>
                    {message}
                </Typography>
                {(status === 'error' || status === 'pending') && (
                    <Button
                        variant="contained"
                        onClick={() => navigate(ROUTES.BRAND_SETTINGS, { replace: true })}
                        sx={{
                            mt: 2,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            bgcolor: t.primary_orange,
                        }}>
                        Back to subscription settings
                    </Button>
                )}
            </Box>
        </MetaTags>
    );
}
